import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'

// Turns every plain same-origin <a> (header, footer, CTA buttons) into client-side navigation,
// so links keep real hrefs for crawlers while the page never reloads. Section links ('#faq',
// '/#faq') scroll smoothly without putting the hash in the URL, navigating home first if needed.
export default function LinkInterceptor() {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  useEffect(() => {
    function onClick(e) {
      const a = e.target.closest('a[href]')
      if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      if (a.target && a.target !== '_self') return
      const href = a.getAttribute('href')
      // Section links on the page they point at just scroll. Checked before building a URL so it
      // also holds when the page is embedded (preview frames, hash routing), where the document's
      // own path differs from the router's.
      const hash = href.startsWith('#') ? href : href.startsWith('/#') && pathname === '/' ? href.slice(1) : null
      if (hash && document.getElementById(hash.slice(1))) {
        e.preventDefault()
        scrollToId(hash.slice(1))
        return
      }
      const url = new URL(href, location.href)
      if (url.origin !== location.origin || a.hasAttribute('download')) return
      e.preventDefault()
      // Keep the hash on cross-page links so the new page's own scroll logic (ScrollToTop) aims at it too.
      if (url.pathname !== pathname) navigate(url.pathname + url.search + url.hash)
      if (url.hash) scrollToId(url.hash.slice(1), url.pathname !== pathname ? document.getElementById(url.hash.slice(1)) : null)
      else if (url.pathname !== pathname) window.scrollTo(0, 0)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [navigate, pathname])

  // Arriving with a hash (shared link): scroll there, then drop it from the address bar.
  useEffect(() => {
    if (!location.hash) return
    scrollToId(location.hash.slice(1))
    history.replaceState(history.state, '', location.pathname + location.search)
  }, [])

  return null
}

// The target may not exist until the next route renders (lazy routes wait on a chunk download),
// so retry for up to 5s. After arriving from another page, images above the section can still be
// loading and push it down, so check again a few times and nudge it back under the header.
// `stale`: a same-id section on the page being left (e.g. the home FAQ when going to /pricing#faq);
// it stays mounted while the next route's chunk loads, so wait for the new one instead.
function scrollToId(id, stale = null, deadline = performance.now() + 5000) {
  const found = document.getElementById(id)
  const el = found !== stale && found
  if (!el) { if (performance.now() < deadline) requestAnimationFrame(() => scrollToId(id, stale, deadline)); return }
  // One smooth scroll straight to the spot under the header.
  const go = () => window.scrollTo({ top: window.scrollY + el.getBoundingClientRect().top - 80, behavior: 'smooth' })
  go()
  // Only once that scroll has finished, correct if images loading above moved the section.
  // Stop as soon as the visitor scrolls on their own.
  let userScrolled = false, fixes = 0
  const stop = () => { userScrolled = true }
  const evs = ['wheel', 'touchstart', 'keydown']
  for (const ev of evs) window.addEventListener(ev, stop, { once: true, passive: true })
  const settle = () => setTimeout(() => {
    const off = Math.abs(el.getBoundingClientRect().top - 80) > 8
    if (!userScrolled && el.isConnected && off && fixes++ < 3) { go(); settle() }
    else for (const ev of evs) window.removeEventListener(ev, stop)
  }, 1000)
  settle()
}
